import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { Badge, Card, Empty, Spinner } from '../components/ui.js';
import { IconImage } from '../components/icons.js';

type Product = {
  id: string;
  code: string;
  name: string;
  family: string;
  material: string;
  basePriceCents: number;
  colorCount: number;
  thumbnailUrl: string | null;
  customizable: boolean;
  active: boolean;
};

const FAMILIES = ['all', 'tshirt', 'sweatshirt', 'cap', 'mug', 'bag', 'other'] as const;
type Family = (typeof FAMILIES)[number];

export function CatalogPage() {
  const t = useT();
  const [items, setItems] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [family, setFamily] = useState<Family>('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  /**
   * Cada búsqueda lleva su número de secuencia. Sin esto, una respuesta lenta de
   * la categoría anterior puede llegar DESPUÉS de la nueva y repintar la lista
   * equivocada: el filtro parece que "hace cosas raras" y en realidad es una
   * carrera entre dos peticiones.
   */
  const peticion = useRef(0);

  useEffect(() => {
    const mia = ++peticion.current;
    setLoading(true);

    api
      .listProducts({
        page,
        pageSize: 24,
        ...(family === 'all' ? {} : { family }),
        ...(debouncedQuery ? { q: debouncedQuery } : {}),
      })
      .then((response) => {
        if (mia !== peticion.current) return; // llegó tarde: se descarta
        setItems(response.items as Product[]);
        setTotal(response.meta.total);
        setError(null);
      })
      .catch((err) => {
        if (mia !== peticion.current) return;
        setError((err as Error).message);
        setItems([]);
      })
      .finally(() => {
        if (mia === peticion.current) setLoading(false);
      });
  }, [family, debouncedQuery, page]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / 24)), [total]);

  const cambiarFamilia = (siguiente: Family) => {
    if (siguiente === family) return;
    setFamily(siguiente);
    setPage(1);
    // Se vacía la lista al cambiar de categoría: dejar la anterior debajo del
    // indicador de carga hacía que pareciera que el filtro no funcionaba.
    setItems([]);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <header className="mb-5">
        <h1 className="text-2xl font-semibold">{t('catalog.title')}</h1>
        <p className="mt-1 text-sm text-tinta-suave">{t('catalog.subtitle')}</p>
      </header>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder={t('catalog.searchPlaceholder')}
          className="toque w-full rounded-lg border border-borde px-3 py-2 text-sm sm:max-w-sm"
        />
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
          {FAMILIES.map((f) => (
            <button
              key={f}
              data-test="familia"
              onClick={() => cambiarFamilia(f)}
              aria-pressed={family === f}
              className={`toque shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                family === f
                  ? 'border-marca bg-marca text-white'
                  : 'border-borde bg-white hover:bg-papel-suave'
              }`}
            >
              {t(`catalog.family.${f}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Un solo estado visible cada vez: cargando, error, vacío o resultados. */}
      {loading ? (
        <Spinner label={t('app.loading')} />
      ) : error ? (
        <Empty title={t('app.error')}>{error}</Empty>
      ) : items.length === 0 ? (
        <Empty title={t('catalog.empty')}>
          {family !== 'all' && (
            <button onClick={() => cambiarFamilia('all')} className="mt-2 underline">
              {t('catalog.family.all')}
            </button>
          )}
        </Empty>
      ) : (
        <>
          <p className="mb-3 text-xs text-tinta-suave">
            {total} {total === 1 ? 'producto' : 'productos'}
          </p>

          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((product) => (
              <li key={product.id}>
                <Link to={`/productos/${product.id}`} className="block h-full">
                  <Card className="flex h-full flex-col transition-shadow hover:shadow-md">
                    <div className="mb-3 flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-papel-suave">
                      {product.thumbnailUrl ? (
                        <img
                          src={product.thumbnailUrl}
                          alt={product.name}
                          loading="lazy"
                          className="h-full w-full object-contain"
                          // Si la URL firmada caducó o el fichero ya no está, se
                          // deja el hueco en vez de un icono de imagen rota.
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <IconImage size={28} className="text-borde" />
                      )}
                    </div>

                    <p className="text-xs text-tinta-suave">{product.code}</p>
                    <p className="line-clamp-2 font-medium">{product.name}</p>
                    <p className="mt-1 line-clamp-1 text-xs text-tinta-suave">{product.material}</p>

                    <div className="mt-auto flex flex-wrap items-center justify-between gap-1 pt-3">
                      <span className="text-xs text-tinta-suave">
                        {t('catalog.colors', { count: product.colorCount })}
                      </span>
                      {product.basePriceCents === 0 ? (
                        <Badge tone="warning">{t('catalog.noPrice')}</Badge>
                      ) : (
                        <span className="text-sm font-semibold tabular-nums">
                          {(product.basePriceCents / 100).toFixed(2)} €
                        </span>
                      )}
                    </div>

                    {/* Sin patrón no hay zona de marcaje: se dice AQUÍ, no
                        después de que el cliente entre y no encuentre el botón. */}
                    {product.customizable ? (
                      /*
                        La tarjeta entera ya es un enlace, pero un enlace no se
                        LEE como una invitación: el cliente miraba la rejilla y
                        no encontraba por ningún lado la palabra "personalizar".
                        Es un adorno (la navegación la hace el enlace de fuera),
                        de ahí el aria-hidden.
                      */
                      <span
                        data-test="personalizar"
                        aria-hidden
                        className="mt-3 block rounded-lg bg-marca px-3 py-2 text-center text-sm font-medium text-white"
                      >
                        {t('catalog.personalize')}
                      </span>
                    ) : (
                      <p className="mt-2 text-xs text-aviso">{t('catalog.notCustomizable')}</p>
                    )}
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}

      {!loading && pages > 1 && (
        <nav className="mt-6 flex items-center justify-center gap-2" aria-label="Paginacion">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="toque rounded-lg border border-borde px-3 py-2 text-sm disabled:opacity-40"
          >
            {t('app.back')}
          </button>
          <span className="text-sm tabular-nums text-tinta-suave">
            {page} / {pages}
          </span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="toque rounded-lg border border-borde px-3 py-2 text-sm disabled:opacity-40"
          >
            {t('app.next')}
          </button>
        </nav>
      )}
    </div>
  );
}
